import { Box, Menu, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArchive,
  IconArchiveOff,
  IconCopy,
  IconHistory,
  IconPencil,
} from "@tabler/icons-react";
import { useRef, type MouseEvent } from "react";
import { RowActionsCell } from "@/components/table/tableCells";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import {
  useArchiveForm,
  useDuplicateForm,
  useUnarchiveForm,
} from "@/hooks/form-builder/useFormLifecycleMutations";
import { FormVersionHistoryDrawer } from "../FormVersionHistoryDrawer/FormVersionHistoryDrawer";
import { DuplicateFormModal } from "./DuplicateFormModal";
import { EditFormDetailsModal } from "../EditFormDetailsModal/EditFormDetailsModal";
import type { FormBuilderListItem } from "@/api/form-builder/formBuilder.types";

interface FormRowActionsProps {
  form: FormBuilderListItem;
  tenantId?: string;
  canManagePlatformForms: boolean;
}

const stopRowClick = (event: MouseEvent) => event.stopPropagation();

export function FormRowActions({ form, tenantId, canManagePlatformForms }: FormRowActionsProps) {
  const [archiveOpened, { open: openArchive, close: closeArchive }] = useDisclosure(false);
  const [duplicateOpened, { open: openDuplicate, close: closeDuplicate }] = useDisclosure(false);
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const [detailsOpened, { open: openDetails, close: closeDetails }] = useDisclosure(false);
  const archive = useArchiveForm();
  const unarchive = useUnarchiveForm();
  const duplicate = useDuplicateForm();

  const target = { formId: form.id, tenantId };
  const isArchived = form.status === "ARCHIVED";
  const canChangeArchiveState = form.origin === "tenant" || canManagePlatformForms;
  const canEditDetails = canChangeArchiveState && !isArchived;

  const containerRef = useRef<HTMLDivElement>(null);

  // The menu item that opened the details modal is gone once the menu closes,
  // so focus goes back to the row's actions button instead of the page start.
  const closeDetailsAndRestoreFocus = () => {
    closeDetails();
    requestAnimationFrame(() =>
      containerRef.current
        ?.querySelector<HTMLButtonElement>('button[aria-label="More actions"]')
        ?.focus(),
    );
  };

  const handleArchive = () => archive.mutate(target, { onSuccess: closeArchive });

  const handleDuplicate = (name: string) =>
    duplicate.mutate({ ...target, name }, { onSuccess: closeDuplicate });

  return (
    <Box ref={containerRef} onClick={stopRowClick}>
      <RowActionsCell>
        {canEditDetails && (
          <Menu.Item leftSection={<IconPencil size={20} />} onClick={openDetails}>
            Edit details
          </Menu.Item>
        )}
        <Menu.Item leftSection={<IconCopy size={20} />} onClick={openDuplicate}>
          Duplicate
        </Menu.Item>
        <Menu.Item
          leftSection={<IconHistory size={20} />}
          disabled={form.status === "DRAFT"}
          onClick={openHistory}
        >
          Version history
        </Menu.Item>
        {canChangeArchiveState && <Menu.Divider />}
        {canChangeArchiveState && isArchived && (
          <Menu.Item
            leftSection={<IconArchiveOff size={20} />}
            disabled={unarchive.isPending}
            onClick={() => unarchive.mutate(target)}
          >
            Unarchive
          </Menu.Item>
        )}
        {canChangeArchiveState && !isArchived && (
          <Menu.Item
            leftSection={<IconArchive size={20} />}
            c="var(--text-danger-default)"
            disabled={form.isFallbackDefault === true}
            onClick={openArchive}
          >
            Archive
          </Menu.Item>
        )}
      </RowActionsCell>
      <ConfirmationModal
        isOpen={archiveOpened}
        onClose={closeArchive}
        title={`Archive "${form.name}"?`}
        body={
          <Text size="sm">
            Archived forms are hidden from the list and cannot be assigned. You can unarchive it
            later from the Archived status filter.
          </Text>
        }
        confirmText="Archive"
        confirmVariant="destructive-primary"
        onConfirm={handleArchive}
        isConfirmLoading={archive.isPending}
        size="md"
      />
      {detailsOpened && (
        <EditFormDetailsModal
          form={form}
          tenantId={tenantId}
          onClose={closeDetailsAndRestoreFocus}
        />
      )}
      {duplicateOpened && (
        <DuplicateFormModal
          sourceName={form.name}
          onClose={closeDuplicate}
          onSubmit={handleDuplicate}
          isSubmitting={duplicate.isPending}
        />
      )}
      <FormVersionHistoryDrawer
        formId={form.id}
        formName={form.name}
        tenantId={tenantId}
        opened={historyOpened}
        onClose={closeHistory}
      />
    </Box>
  );
}
